import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, LogOut, Settings } from 'lucide-react';
import { NAVIGATION, type NavLeaf, type NavSection } from '@/navigation';
import { useAuth } from '@/auth/AuthProvider';
import { useAgency } from '@/auth/AgencyProvider';
import { usePermissions } from '@/auth/PermissionsProvider';
import { useProfile } from '@/data/useProfile';
import { useChatUnread } from '@/realtime/ChatUnread';
import { badgeText } from '@/lib/chat';
import { cn } from '@/lib/utils';
import { Wordmark } from './Wordmark';

/**
 * The rail: docs/estilo-visual.md §Layout.
 *
 * Fixed, dark, --size-rail-w wide, with the wordmark at the top, the menu in the middle and the user
 * card at the bottom. The active item is a SOLID BLUE PILL — not an underline, not a border.
 */
/** 'rail' is the desktop column; 'drawer' is the same menu inside the phone drawer (MobileNav), where
 *  every icon button grows to the 44 px touch target. */
type SidebarVariant = 'rail' | 'drawer';
const VariantContext = React.createContext<SidebarVariant>('rail');

/** The square icon buttons of the rail: 32 px on the desktop, the 44 px touch target in the drawer. */
function iconButtonSize(variant: SidebarVariant) {
  return variant === 'drawer' ? 'size-touch' : 'size-8';
}

function itemClasses(active: boolean, nested: boolean) {
  return cn(
    'flex h-row items-center gap-3 rounded-md px-3 text-base transition-colors duration-(--duration-fast) ease-out',
    nested && 'pl-9 text-sm',
    active
      ? 'bg-accent-base font-medium text-on-accent'
      : 'text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
  );
}

function LeafLink({ item, nested }: { item: NavLeaf; nested: boolean }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink to={item.path} end={!item.coversSubpaths} className={({ isActive }) => itemClasses(isActive, nested)}>
      <Icon className={nested ? 'size-4' : 'size-5'} aria-hidden="true" />
      <span className="truncate">{t(`nav.${item.id}`)}</span>
      {item.id === 'chat' && <ChatBadge />}
    </NavLink>
  );
}

/** Unread conversations on the Chat entry (driver-office-chat, design 7.1): 1..99, then 99+. */
function ChatBadge() {
  const { t } = useTranslation();
  const chat = useChatUnread();
  const text = chat ? badgeText(chat.unreadThreads) : null;
  if (!text) return null;
  return (
    <span
      className="ml-auto min-w-5 rounded-full bg-danger px-1.5 text-center text-xs font-semibold leading-5 text-white"
      aria-label={t('chat.badge', { count: chat?.unreadThreads ?? 0 })}
      data-testid="chat-badge"
    >
      {text}
    </span>
  );
}

function Section({ section }: { section: NavSection }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const children = section.children?.filter((c) => !c.gate || can(c.gate));
  const [open, setOpen] = React.useState(section.defaultOpen ?? false);
  const panelId = `nav-${section.id}`;
  const label = t(`nav.${section.id}`);
  const Chevron = open ? ChevronDown : ChevronRight;
  const variant = React.useContext(VariantContext);

  if (!children) return <LeafLink item={section} nested={false} />;

  return (
    <div>
      <div className="flex items-center">
        <div className="min-w-0 flex-1">
          <LeafLink item={section} nested={false} />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={t(open ? 'nav.collapse' : 'nav.expand', { section: label })}
          className={cn(
            'ml-1 grid shrink-0 place-items-center rounded-md text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
            iconButtonSize(variant),
          )}
        >
          <Chevron className="size-4" aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div id={panelId} className="mt-1 flex flex-col gap-1">
          {children.map((child) => (
            <LeafLink key={child.id} item={child} nested />
          ))}
        </div>
      )}
    </div>
  );
}

function UserCard() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const agency = useAgency();
  const email = user?.email ?? '';
  // The profile name the team sees (design 5.3); the email stands in until there is one, and stays in
  // the title either way.
  const { profile } = useProfile();
  const shown = profile?.displayName || email;
  // The role of the ACTIVE membership in the current agency (AgencyProvider), the same one the server
  // honours. No membership says so rather than inventing a badge.
  const roleLabel =
    agency.status === 'ready'
      ? t(`roles.${agency.role}`)
      : agency.status === 'loading'
        ? t('user.roleLoading')
        : agency.status === 'none'
          ? t('user.roleNone')
          : agency.status === 'suspended'
            ? t('user.roleSuspended')
          : t('user.roleUnknown');
  const initial = shown.slice(0, 1).toUpperCase() || '?';
  const variant = React.useContext(VariantContext);

  return (
    <div className="flex shrink-0 items-center gap-3 border-t border-rail-2 px-3 py-4">
      <span
        className="grid size-10 shrink-0 place-items-center rounded-full bg-rail-2 text-base font-semibold text-on-rail"
        aria-hidden="true"
      >
        {initial}
      </span>
      <span className="min-w-0 flex-1">
        <span title={email} className="block truncate text-base font-medium text-on-rail">
          {shown}
        </span>
        <span title={roleLabel} className="block truncate text-sm text-on-rail-muted">
          {roleLabel}
        </span>
      </span>
      <NavLink
        to="/settings"
        title={t('user.settings')}
        aria-label={t('user.settings')}
        className={({ isActive }) =>
          cn(
            'grid shrink-0 place-items-center rounded-md',
            iconButtonSize(variant),
            isActive ? 'bg-accent-base text-on-accent' : 'text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
          )
        }
      >
        <Settings className="size-5" aria-hidden="true" />
      </NavLink>
      <button
        type="button"
        onClick={() => void signOut()}
        title={t('user.signOut')}
        aria-label={t('user.signOut')}
        className={cn(
          'grid shrink-0 place-items-center rounded-md text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
          iconButtonSize(variant),
        )}
      >
        <LogOut className="size-5" aria-hidden="true" />
      </button>
    </div>
  );
}

export function Sidebar({ variant = 'rail' }: { variant?: SidebarVariant }) {
  const { t } = useTranslation();
  // The chat is not offered to accounting (driver-office-chat, Hector decision 2).
  const chat = useChatUnread();
  const agency = useAgency();
  const hideChat = agency.status === 'ready' && agency.role === 'accounting' && (chat === null || !chat.enabled);

  return (
    <VariantContext.Provider value={variant}>
    {/* Rail: sticky and h-screen, not fixed: the navy footer has to be able to run underneath it (see
        AppLayout). Drawer: the full height of the drawer panel, which is itself fixed. */}
    <aside
      className={cn(
        'flex w-full shrink-0 flex-col bg-rail',
        variant === 'rail' ? 'sticky top-0 z-(--z-sticky) h-screen w-rail' : 'h-full',
      )}
    >
      <div className={cn('shrink-0 px-5 pt-5 pb-4', variant === 'drawer' && 'pr-14')}>
        <Wordmark tone="rail" />
        <p className="mt-2 text-sm text-on-rail-muted">{t('brand.tagline')}</p>
      </div>

      {/* The scroller and the list are SEPARATE elements on purpose. With the items as direct flex
          children of a height-constrained column they shrink below --size-row-h instead of
          overflowing: a 44px row silently became 21px. The inner list sizes to its content and the
          nav scrolls around it. */}
      <nav aria-label={t('brand.product')} className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <div className="flex flex-col gap-1">
          {NAVIGATION.filter((section) => !(hideChat && section.id === 'chat')).map((section) => (
            <Section key={section.id} section={section} />
          ))}
        </div>
      </nav>

      <UserCard />

      <p className="shrink-0 px-5 pt-4 pb-5 text-lg leading-tight font-bold text-on-rail">
        {t('brand.mottoLine1')}
        <br />
        <span className="text-accent-base">{t('brand.mottoLine2')}</span>
        <br />
        {t('brand.mottoLine3')}
      </p>
    </aside>
    </VariantContext.Provider>
  );
}
